import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  ErrorSummary,
  Input,
  postOrderLog,
  type ErrorSummaryItem,
} from '@alwindows/shared'
// Focus management mirrors spare-parts Checkout: when the page transitions
// from "submitting" to "done", AT users benefit from focus moving to the
// success heading so the state change is announced (WCAG 4.1.3 / 2.4.3).
import {
  postScreenSaveOrder,
  type HanaScreenLineItem,
  type IdocScreenItem,
  type ScreenLineAction,
  type ScreenSaveOrderPayload,
} from '@alwindows/shared/api/screens'
import { useQuote } from '../quote/QuoteContext'
import { ROUTE_PAYMENT, ROUTE_SELECT_SCREENS, ROUTE_SUMMARY } from '../routes'
import { trackSaveOrder } from '../analytics'
import {
  effectiveInstallCost,
  getConfigValue,
  lineDisplayName,
  lineTotal,
  prodFamFromConfig,
  type ScreenLine,
} from '../quote/types'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// AU phone number regex — copied verbatim from the legacy app
// (al_screens/src/utils/Helper.js:350 `isValidAusPhone`). Matches landline +
// mobile + the (+61) prefix variants; rejects "abc", short strings, and
// random punctuation. Kept identical so SAP receives the same validated
// shape it did under the legacy app.
const AUS_PHONE_RE =
  /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/

// Same WCAG 2.2 error-summary pattern as spare-parts Checkout: one
// validation pass collects every failure, surfaced in <ErrorSummary> at the
// top (focused on submit failure) AND on each input.
type FieldKey = 'firstName' | 'lastName' | 'email' | 'phone' | 'agree'

const FIELD_LABELS: Record<FieldKey, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  agree: 'Terms and conditions',
}

const FIELD_IDS: Record<FieldKey, string> = {
  firstName: 'submit-first-name',
  lastName: 'submit-last-name',
  email: 'submit-email',
  phone: 'submit-phone',
  agree: 'submit-agree',
}

/**
 * Details + submit step — live-site parity (legacy ScreenSubmit): collects
 * name / email / phone / comments / terms here, AFTER the Summary review,
 * and only fires saveOrder when the form validates. The Summary page is
 * review-only.
 */
export function Submit() {
  const { quote, setQuote, reset } = useQuote()
  const navigate = useNavigate()
  // The applied promo discount is persisted on the quote by Summary — NOT
  // router state, which a refresh of this page would silently drop.
  const passedDiscount = quote.promoAmount || 0
  const debug = typeof window !== 'undefined' && Boolean(window.AL_WINDOWS_CONFIG?.debug)

  const [status, setStatus] = useState<'form' | 'submitting' | 'done' | 'error'>('form')
  const [error, setError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
  const [agree, setAgree] = useState(false)
  const [refNo, setRefNo] = useState<string | null>(quote.refNo)
  const successHeadingRef = useRef<HTMLHeadingElement>(null)
  const errorSummaryRef = useRef<HTMLDivElement>(null)
  // In-flight guard: a rapid double-click within React's batched-render gap
  // would otherwise fire postScreenSaveOrder twice and SAP records two orders.
  const submittingRef = useRef(false)

  const c = quote.customer
  const selected = quote.lines.filter((l) => l.selected && l.screenOption)

  // The quote's customer (address bits) is created at the address step; if
  // it's missing the user deep-linked here — send them to the start. Debug
  // mode stays put so testers can see the page chrome by typing the URL.
  useEffect(() => {
    if (!quote.customer && !debug) navigate('/', { replace: true })
  }, [quote.customer, debug, navigate])

  // Already-saved quote (refresh after success) → straight to confirmation.
  useEffect(() => {
    if (quote.refNo) {
      setRefNo(quote.refNo)
      setStatus('done')
    }
  }, [quote.refNo])

  // Move focus to the success heading when the order completes so screen
  // readers announce the transition. rAF defers until after the heading is
  // in the DOM following the status flip.
  useEffect(() => {
    if (status === 'done') {
      window.requestAnimationFrame(() => successHeadingRef.current?.focus())
    }
  }, [status])

  const updateCustomer = (patch: Partial<NonNullable<typeof c>>) => {
    setQuote((prev) => ({
      ...prev,
      customer: prev.customer ? { ...prev.customer, ...patch } : null,
    }))
  }

  const clearError = (key: FieldKey) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Single-pass validation: surfaces every failure at once so the summary
  // lists them all rather than the user fixing one to hit the next.
  const validate = (): Partial<Record<FieldKey, string>> => {
    const e: Partial<Record<FieldKey, string>> = {}
    if (!c?.firstName?.trim()) e.firstName = 'Enter your first name'
    if (!c?.lastName?.trim()) e.lastName = 'Enter your last name'
    if (!c?.email?.trim()) e.email = 'Enter your email address'
    else if (!EMAIL_RE.test(c.email)) e.email = 'Enter a valid email address'
    if (!c?.phone?.trim()) e.phone = 'Enter your phone number'
    else if (!AUS_PHONE_RE.test(c.phone)) e.phone = 'Enter a valid Australian phone number'
    if (!agree) e.agree = 'You must agree to the terms and conditions'
    return e
  }

  const handleEmailBlur = () => {
    if (!c?.email?.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Enter your email address' }))
    } else if (!EMAIL_RE.test(c.email)) {
      setErrors((prev) => ({ ...prev, email: 'Enter a valid email address' }))
    }
  }

  const handlePhoneBlur = () => {
    if (!c?.phone?.trim()) {
      setErrors((prev) => ({ ...prev, phone: 'Enter your phone number' }))
    } else if (!AUS_PHONE_RE.test(c.phone)) {
      setErrors((prev) => ({ ...prev, phone: 'Enter a valid Australian phone number' }))
    }
  }

  const submit = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setStatus('submitting')
    setError(null)
    try {
      const payload = buildPayload(quote, passedDiscount)
      const result = await postScreenSaveOrder(payload)
      setRefNo(result.RefNo)
      setQuote((p) => ({ ...p, refNo: result.RefNo }))
      trackSaveOrder(result.RefNo) // legacy `save_order` GTM event

      // Local WP order log (anonymous). `total` is what the customer will
      // actually pay — screens + install − promo — unlike SAP's TotalPrice
      // field, which (legacy-faithfully) carries the screen subtotal only.
      const charged =
        selected.reduce((s, l) => s + lineTotal(l), 0) +
        effectiveInstallCost(quote) -
        passedDiscount
      void postOrderLog({
        app: 'screens',
        order_number: result.RefNo,
        line_items: selected.map((l) => ({
          sku: l.sourceItem.Material,
          qty: l.qty,
          price: lineTotal(l) / l.qty,
          name: lineDisplayName(l),
        })),
        total: charged,
      })

      setStatus('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed.')
      setStatus('error')
    } finally {
      submittingRef.current = false
    }
  }

  const handleSubmit = () => {
    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      // rAF so the summary has mounted before we move focus to it.
      window.requestAnimationFrame(() => errorSummaryRef.current?.focus())
      return
    }
    void submit()
  }

  if (status === 'submitting') {
    return (
      <div className="al-submit text-center" aria-live="polite" aria-atomic="true">
        <h2>Saving your quote…</h2>
        <p>Hold on a moment.</p>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="al-submit text-center">
        <h2>Something went wrong</h2>
        <Alert variant="error">{error}</Alert>
        <div className="al-sc__actions">
          <Button variant="secondary" onClick={() => setStatus('form')}>
            Back to your details
          </Button>
          <Button variant="primary" onClick={() => void submit()}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (status === 'done') {
    return (
      <div className="al-submit">
        <h2 ref={successHeadingRef} tabIndex={-1}>
          Quote saved
        </h2>
        <Alert variant="success" title={`Reference: ${refNo ?? ''}`}>
          We&rsquo;ve emailed your quote to <strong>{quote.customer?.email}</strong>. When
          you&rsquo;re ready to pay, come back to the payment page with your reference number and
          surname.
        </Alert>
        <div className="al-sc__actions">
          {/* Same pairing as Summary/cart: neutral outline for the reset
              action, red outline for the forward CTA. */}
          <Button
            variant="outline"
            onClick={() => {
              reset()
              navigate('/')
            }}
          >
            Start over
          </Button>
          <Button variant="primary-outline" fill asChild>
            <Link to={ROUTE_PAYMENT}>Pay now</Link>
          </Button>
        </div>
      </div>
    )
  }

  // Details form — field layout and input styling follow the spare-parts
  // Checkout conventions (theme `row`/`col-*` grid, `input-text` class).
  return (
    <div className="al-submit al-submit--form">
      <h2>Your details</h2>

      <ErrorSummary
        ref={errorSummaryRef}
        items={(Object.keys(errors) as FieldKey[]).map<ErrorSummaryItem>((k) => ({
          key: k,
          label: FIELD_LABELS[k],
          message: errors[k]!,
          targetId: FIELD_IDS[k],
        }))}
      />

      {!c && debug ? (
        <Alert variant="info" title="No customer on the quote">
          Start from the <Link to="/">address step</Link> (or seed <code>quote.customer</code> in
          sessionStorage) to fill this form with data.
        </Alert>
      ) : null}

      <p className="al-submit__addr m-bx6">
        Installation address:{' '}
        <strong>
          {c?.street}, {c?.city} {c?.state} {c?.postcode}
        </strong>
      </p>

      <section>
        <h3>Contact details</h3>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.firstName}
              label="First name"
              required
              autoComplete="given-name"
              maxLength={70}
              error={errors.firstName}
              value={c?.firstName ?? ''}
              onChange={(e) => {
                updateCustomer({ firstName: e.target.value })
                clearError('firstName')
              }}
              className="input-text"
            />
          </div>
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.lastName}
              label="Last name"
              required
              autoComplete="family-name"
              maxLength={70}
              error={errors.lastName}
              value={c?.lastName ?? ''}
              onChange={(e) => {
                updateCustomer({ lastName: e.target.value })
                clearError('lastName')
              }}
              className="input-text"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.email}
              label="Email"
              type="email"
              required
              autoComplete="email"
              maxLength={241}
              error={errors.email}
              value={c?.email ?? ''}
              onChange={(e) => {
                updateCustomer({ email: e.target.value })
                clearError('email')
              }}
              onBlur={handleEmailBlur}
              className="input-text"
            />
          </div>
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.phone}
              label="Phone"
              type="tel"
              required
              autoComplete="tel"
              maxLength={30}
              error={errors.phone}
              value={c?.phone ?? ''}
              onChange={(e) => {
                updateCustomer({ phone: e.target.value })
                clearError('phone')
              }}
              onBlur={handlePhoneBlur}
              className="input-text"
            />
          </div>
        </div>
      </section>

      <section className="m-tx4">
        <h3>Comments</h3>
        <label htmlFor="submit-comments" className="al-field__label">
          Anything else we should know about your installation?{' '}
          <span className="al-field__hint">(optional)</span>
        </label>
        <textarea
          id="submit-comments"
          className="al-submit__comments input-text"
          rows={4}
          maxLength={500}
          value={quote.comments}
          onChange={(e) => setQuote((p) => ({ ...p, comments: e.target.value }))}
        />
        <p className="al-submit__comments-counter xsmall text-right">
          {quote.comments.length} / 500
        </p>
      </section>

      {/* Installation time-frame note — hardcoded verbatim from the legacy
          ScreenSubmit.js (not API-driven). Live renders it between the
          comments and the terms checkbox. */}
      <div className="m-tx4">
        <p className="bold no-m-b">Please note</p>
        <p className="no-m-t">
          Installation will be 2-4 weeks from the time you submit this order. Subject to additional
          wait times during our Christmas shut down period and other seasonal closures.
        </p>
      </div>

      <section className="m-tx4">
        <input
          id={FIELD_IDS.agree}
          type="checkbox"
          className="input-checkbox"
          checked={agree}
          aria-invalid={errors.agree ? true : undefined}
          aria-describedby={errors.agree ? `${FIELD_IDS.agree}-err` : undefined}
          onChange={(e) => {
            setAgree(e.target.checked)
            clearError('agree')
          }}
        />
        <label htmlFor={FIELD_IDS.agree} className="al-submit__agree">
          I have read and agree to A&amp;L&rsquo;s{' '}
          <a
            href="https://www.alwindows.com.au/terms-and-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            className="al-submit__terms-link"
          >
            terms and conditions of sale
          </a>
        </label>
        {errors.agree ? (
          <p id={`${FIELD_IDS.agree}-err`} role="alert" className="al-field__error">
            {errors.agree}
          </p>
        ) : null}
      </section>

      {selected.length === 0 ? (
        <Alert variant="error" title="No screens selected">
          Your quote has no configured screens. <Link to={ROUTE_SELECT_SCREENS}>Go back</Link> and choose
          your screens first.
        </Alert>
      ) : null}

      <div className="al-sc__actions">
        <Button variant="outline" asChild>
          <Link to={ROUTE_SUMMARY}>Back</Link>
        </Button>
        <Button variant="primary" disabled={selected.length === 0 || !c} onClick={handleSubmit}>
          Submit quote
        </Button>
      </div>
    </div>
  )
}

function buildPayload(
  quote: ReturnType<typeof useQuote>['quote'],
  discount: number,
): ScreenSaveOrderPayload {
  const c = quote.customer
  if (!c) {
    // The form's Submit button is disabled without a customer; this throw
    // covers the synchronous path and keeps strict-TS honest at the seam.
    throw new Error('buildPayload requires quote.customer.')
  }
  const selected = quote.lines.filter((l) => l.selected && l.screenOption)
  // Effective fee (doubled for INSTALL_DOUBLE_FAM) — matches what the cart
  // bar and Summary showed the customer, and what legacy sent SAP.
  const installCost = effectiveInstallCost(quote)
  // Legacy TotalPrice is the SCREEN SUBTOTAL only (Helper.getSubTotal) —
  // install and promo ride in their own InstallCost / PromoAmount fields,
  // so including them here would double-count on SAP's side.
  const total = selected.reduce((s, l) => s + lineTotal(l), 0)

  // Per-line Action — the legacy item STATUS strings, passed verbatim.
  // Legacy sent EVERY item from the home (not just the chosen ones), each
  // tagged with its state, so SAP sees the full inventory. An applicable
  // opening the customer did not order is "Cancelled", whether or not they
  // ever opened its configurator: legacy never let one reach SAP as
  // "Not Selected" — the "proceed with partial selection" step flipped every
  // unpicked applicable item to Cancelled (EditorOverview.
  // handleProceedWithPartialSelection) and Summary/Submit bounced back to the
  // overview while any remained.
  const lineAction = (line: ScreenLine): ScreenLineAction => {
    if (!line.sourceItem.ScreenApply || line.sourceItem.FixedWindow) return 'Not Applicable'
    if (line.selected && line.screenOption) return 'Selected'
    return 'Cancelled'
  }

  const placeholder = { Price: 0, ProdType: '', ProdFam: '', ProdDesc: '', NonTradeCharge: 0 }

  // DefaultFrameColor is the home's EXISTING window/door frame colour: the
  // FM_ALU_COLOUR Value on the first sales-order item, exactly as legacy
  // sent it (PricingCalculator.loadData → setDefaultFrame). It is not
  // derived from the screens the customer picked — each ToScrItm line
  // carries its own FrameColor for that. Same value the Select screens and
  // Summary headers show as "Window & door frame colour".
  const defaultFrameColor = getConfigValue(quote.lines[0]?.sourceConfig ?? [], 'FM_ALU_COLOUR')

  const ToScrItm: HanaScreenLineItem[] = quote.lines.map((line) => ({
    MaterialNo: line.sourceItem.Material,
    Qty: line.qty,
    Price: line.unitPrice.toFixed(2),
    SONO: line.sourceItem.SONo,
    SOItemNo: line.sourceItem.ItemNo,
    // Colour NAME ("White") — legacy parity; the CODE goes in the Idoc
    // half's ColourCode. Falls back to the code for pre-existing quotes.
    FrameColor: line.frameColourDesc || line.frameColour,
    ProdType: line.sourceItem.ProdType,
    // Legacy createLineItem: ProdFam = the PROD_FAM CHARACTERISTIC ('WS'),
    // ProdDesc = the PROD_FAM VALUE ("Window Sliding"). The raw item-level
    // ProdDesc ('W01') is the RoomName seed, not the product name. Both must
    // be defined strings — JSON.stringify drops undefined keys and SAP's
    // parser 500s on the missing field.
    ProdFam: prodFamFromConfig(line.sourceConfig) || '',
    ProdDesc: lineDisplayName(line),
    Height: configValue(line, 'FRAME_HT'),
    Width: configValue(line, 'FRAME_WID'),
    Screen: line.screenOption,
    ScreenDesc: line.screenOptionDesc || line.screenOption,
    Action: lineAction(line),
    IsCharactImage: line.sourceItem.IsCharactImage ? 1 : 0,
    Image: line.sourceItem.Image,
    HasTripleLock: line.hasTripleLock ? 1 : 0,
    FitCost: line.fitCost,
    TripleLockCost: line.hasTripleLock ? line.tripleLockCost : 0,
    NonTradeCharge: line.nonTradeCharge,
    // Sub-option prices cached on the line when picked — legacy sent the
    // option row's Price/NonTradeCharge here, not zeros.
    ToGrill: line.grillType
      ? [
          {
            Price: line.grillPrice || 0,
            ProdType: line.grillType,
            ProdFam: line.grillProdFam || '',
            ProdDesc: line.grillDesc || '',
            NonTradeCharge: line.grillNonTrade || 0,
          },
        ]
      : [placeholder],
    ToMesh: line.meshType
      ? [
          {
            Price: line.meshPrice || 0,
            ProdType: line.meshType,
            ProdFam: line.meshProdFam || '',
            ProdDesc: line.meshDesc || '',
            NonTradeCharge: line.meshNonTrade || 0,
          },
        ]
      : [placeholder],
    ToPetflap: line.petFlapType
      ? [
          {
            Price: line.petFlapPrice || 0,
            ProdType: line.petFlapType,
            ProdFam: line.petFlapProdFam || '',
            ProdDesc: line.petFlapDesc || '',
            NonTradeCharge: line.petFlapNonTrade || 0,
          },
        ]
      : [placeholder],
    // The customer's own label for the opening ("Kitchen window"), edited on
    // the Select screens list; falls back to the product display name for
    // quotes persisted before roomName existed.
    RoomName: line.roomName || lineDisplayName(line),
  }))

  const QuotationToScreen: IdocScreenItem[] = quote.lines.map((line) => ({
    Action: lineAction(line),
    SONo: line.sourceItem.SONo,
    ItemNo: line.sourceItem.ItemNo,
    BalRating: line.balRating,
    ProdFam: line.sourceItem.ProdType,
    ProdType: line.screenOption,
    SalesOrg: c.salesOrg,
    ScreenToMesh: line.meshType
      ? [{ ProdType: line.meshType, SONo: line.sourceItem.SONo, ItemNo: line.sourceItem.ItemNo }]
      : [],
    ScreenToGrill: line.grillType
      ? [{ ProdType: line.grillType, SONo: line.sourceItem.SONo, ItemNo: line.sourceItem.ItemNo }]
      : [],
    ScreenToPetFlap: line.petFlapType
      ? [{ ProdType: line.petFlapType, SONo: line.sourceItem.SONo, ItemNo: line.sourceItem.ItemNo }]
      : [],
    HasTripleLock: line.hasTripleLock,
    ColourCode: line.frameColour,
  }))

  return {
    HanaPayload: {
      SalesOrg: c.salesOrg,
      FName: c.firstName,
      LName: c.lastName,
      Comments: quote.comments,
      BalRating: selected[0]?.balRating ?? 'LOW',
      Status: 'Pending',
      TelNumber: c.phone,
      Street: c.street,
      PostCode: c.postcode,
      City: c.city,
      State: c.state,
      TotalPrice: total.toFixed(2),
      PaymentStatus: '',
      ShipmentType: '',
      ShipmentValue: '',
      Email: c.email,
      DefaultFrameColor: defaultFrameColor,
      AddrNo: c.addrNo,
      PaymentURL: '',
      InstallCost: installCost,
      PromoCode: quote.promoCode,
      PromoAmount: discount,
      ToScrItm,
    },
    IdocPayload: {
      RefNo: '',
      FirstName: c.firstName,
      LastName: c.lastName,
      Email: c.email,
      TelNumber: c.phone,
      Street: c.street,
      City: c.city,
      State: c.state,
      PostCode: c.postcode,
      Country: 'AU',
      PromoCode: quote.promoCode,
      QuotationToScreen,
    },
  }
}

function configValue(line: ScreenLine, name: string): string {
  return line.sourceConfig.find((c) => c.Name === name)?.Value ?? ''
}
